import { StandardEvents } from '@shopify/events';

/**
 * Product page behaviour for the "AG · Product" section.
 *
 * Finish and size are variant options. Picking one resolves the variant from
 * the JSON the section embeds, re-renders the section through the Section
 * Rendering API with `?variant=` and swaps only the parts that depend on the
 * selection (`[data-ag-part]`), so the gallery keeps its state and every price
 * stays a Liquid-formatted, market-aware value. Adding to cart is left to
 * Horizon's `product-form-component`, which wraps the form.
 */
class AgProductComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #fetch = null;
  /** @type {AbortController} */
  #ac = new AbortController();
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {number | undefined} */
  #addedTimer;

  connectedCallback() {
    const { signal } = this.#ac;
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.#observeBuyBox();
    this.#warmGallery();
    // Let the compare cards mirror the initial selection.
    requestAnimationFrame(() => this.#announce());
  }

  #announce() {
    document.dispatchEvent(
      new CustomEvent('ag:variant', {
        detail: { finish: this.finish, size: this.size, unit: this.unit },
      })
    );
  }

  disconnectedCallback() {
    this.#ac.abort();
    this.#io?.disconnect();
  }

  get variants() {
    if (!this._variants) {
      const script = this.querySelector('script[data-ag-variants]');
      try {
        this._variants = JSON.parse(script?.textContent || '[]');
      } catch {
        this._variants = [];
      }
    }
    return /** @type {Array<{id: number, f: string, z: string, a: boolean}>} */ (this._variants);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const thumb = target.closest('[data-ag-thumb]');
    if (thumb instanceof HTMLButtonElement) return this.#showThumb(thumb);

    const finish = target.closest('[data-ag-finish]');
    if (finish instanceof HTMLElement) return this.selectFinish(finish.dataset.agFinish || '');

    const size = target.closest('[data-ag-size]');
    if (size instanceof HTMLElement) return this.#selectSize(size.dataset.agSize || '');

    const unit = target.closest('[data-ag-unit]');
    if (unit instanceof HTMLElement) return this.#switchUnit(unit.dataset.agUnit || 'cm');
  };

  /** Compare-finish buttons live in another section. */
  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const pick = target.closest('[data-ag-pick-finish]');
    if (!(pick instanceof HTMLElement) || this.contains(pick)) return;
    event.preventDefault();
    this.selectFinish(pick.dataset.agPickFinish || '');
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * The main frame swaps to a full-size source on every thumbnail click. Left
   * to the click itself that is a ~500ms network wait; warmed ahead of time it
   * lands in a frame or two. Hovering a thumbnail warms it immediately, and
   * the whole strip is warmed once the page goes idle.
   * @param {Element} thumb
   */
  #warmThumb(thumb) {
    if (!(thumb instanceof HTMLElement) || thumb.dataset.warm) return;
    thumb.dataset.warm = '1';
    const main = this.querySelector('.ag-gallery__main img');
    const image = new Image();
    if (main instanceof HTMLImageElement) image.sizes = main.sizes;
    if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
    if (thumb.dataset.src) image.src = thumb.dataset.src;
  }

  #warmGallery() {
    // Every thumbnail's full source is 1–3 MB together. Worth it where the
    // swap is seen on a large screen; not on a phone or a metered connection,
    // where hovering does not exist and the tap still warms its own image.
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData || /(^|-)[23]g$/.test(connection?.effectiveType || '')) return;
    if (!window.matchMedia('(hover: hover) and (min-width: 750px)').matches) return;
    // Three, not the whole strip: the first swaps are the ones that must be
    // instant, hovering warms any other, and no timeout — a forced burst
    // during load competes with the images still streaming in below.
    const warm = () =>
      Array.from(this.querySelectorAll('[data-ag-thumb]:not(.is-active)'))
        .slice(0, 3)
        .forEach((t) => this.#warmThumb(t));
    if ('requestIdleCallback' in window) requestIdleCallback(warm);
    else setTimeout(warm, 2000);
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumb = target.closest('[data-ag-thumb]');
    if (thumb) this.#warmThumb(thumb);
  };

  /** @param {HTMLButtonElement} thumb */
  #showThumb(thumb) {
    const main = this.querySelector('.ag-gallery__main img');
    const caption = this.querySelector('[data-ag-caption]');
    if (main instanceof HTMLImageElement) {
      // srcset first: setting src while the old srcset is still in place makes
      // the browser run selection twice, the first time against the old image.
      if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
      main.src = thumb.dataset.src || main.src;
      main.alt = thumb.dataset.alt || '';
    }
    if (caption instanceof HTMLElement) {
      const text = thumb.dataset.caption || '';
      caption.textContent = text;
      caption.hidden = !text;
    }
    this.querySelectorAll('[data-ag-thumb]').forEach((el) => {
      const active = el === thumb;
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }

  get finish() {
    return this.dataset.finish || '';
  }
  get size() {
    return this.dataset.size || '';
  }
  get unit() {
    return this.dataset.unit || 'cm';
  }

  /** Size values in display order for a unit group, read from the DOM. */
  /** @param {string} unit */
  #sizesFor(unit) {
    return Array.from(this.querySelectorAll(`[data-ag-sizes="${unit}"] [data-ag-size]`)).map(
      (el) => /** @type {HTMLElement} */ (el).dataset.agSize || ''
    );
  }

  /** @param {string} finish */
  selectFinish(finish) {
    if (!finish || finish === this.finish) return;
    const variants = this.variants;
    let match = variants.find((v) => v.f === finish && v.z === this.size);
    if (!match) {
      const sizes = this.#sizesFor(this.unit);
      match = sizes.map((z) => variants.find((v) => v.f === finish && v.z === z)).find((v) => v && v.a);
    }
    if (!match) match = variants.find((v) => v.f === finish);
    if (match) this.#change(match.id);
  }

  /** @param {string} size */
  #selectSize(size) {
    if (!size || size === this.size) return;
    const match = this.variants.find((v) => v.z === size && (this.finish ? v.f === this.finish : true));
    if (match) this.#change(match.id);
  }

  /** @param {string} unit */
  #switchUnit(unit) {
    if (unit === this.unit) return;
    const from = this.#sizesFor(this.unit);
    const to = this.#sizesFor(unit);
    const index = Math.max(0, from.indexOf(this.size));
    const size = to[Math.min(index, to.length - 1)];
    if (size) this.#selectSize(size);
  }

  /** @param {number} variantId */
  async #change(variantId) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#fetch?.abort();
    this.#fetch = new AbortController();
    this.classList.add('is-loading');

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url, { signal: this.#fetch.signal });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('ag-product-component');
      if (!next) throw new Error('Section markup missing');

      this.#swapParts(next);

      const input = this.querySelector('[data-ag-variant-input]');
      if (input instanceof HTMLInputElement) input.value = String(variantId);

      for (const key of ['finish', 'size', 'unit']) {
        const value = next.dataset[key];
        if (value !== undefined) this.dataset[key] = value;
      }

      // Re-apply the caption of the active thumbnail (the first one carries the size).
      const active = this.querySelector('[data-ag-thumb].is-active');
      const caption = this.querySelector('[data-ag-caption]');
      if (active instanceof HTMLButtonElement && caption instanceof HTMLElement) {
        const text = active.dataset.caption || '';
        caption.textContent = text;
        caption.hidden = !text;
      }

      const clean = new URL(window.location.href);
      clean.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', clean);

      this.#announce();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.warn('[ag-product]', error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /** @param {Element} next */
  #swapParts(next) {
    next.querySelectorAll('[data-ag-part]').forEach((part) => {
      const name = part.getAttribute('data-ag-part');
      const current = this.querySelector(`[data-ag-part="${name}"]`);
      if (!current) return;
      // Keep the live variant input Horizon references; it is updated separately.
      if (name === 'cta') {
        current.innerHTML = part.innerHTML;
        return;
      }
      current.replaceChildren(...Array.from(part.childNodes));
      Array.from(part.attributes).forEach((attr) => {
        if (attr.name !== 'data-ag-part') current.setAttribute(attr.name, attr.value);
      });
    });
    // The thumbnails carry the size caption; refresh their data attributes too.
    const nextThumbs = next.querySelectorAll('[data-ag-thumb]');
    this.querySelectorAll('[data-ag-thumb]').forEach((thumb, i) => {
      const source = nextThumbs[i];
      if (source instanceof HTMLElement && thumb instanceof HTMLElement) {
        thumb.dataset.caption = source.dataset.caption || '';
      }
    });
  }

  #observeBuyBox() {
    const sticky = this.querySelector('[data-ag-sticky]');
    const buy = this.querySelector('[data-ag-buy]');
    if (!(sticky instanceof HTMLElement) || !(buy instanceof HTMLElement)) return;

    this.#io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.hidden = !show;
        document.documentElement.classList.toggle('ag-has-sticky', show);
      },
      { threshold: 0 }
    );
    this.#io.observe(buy);
  }

  /** @param {'adding' | 'added' | ''} state */
  #setCtaState(state) {
    const label = this.dataset.addedLabel || 'Added to cart ✓';
    this.querySelectorAll('[data-ag-cta]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (state) button.dataset.state = state;
      else delete button.dataset.state;
      const text = button.querySelector('[data-ag-cta-label]');
      if (!(text instanceof HTMLElement)) return;
      if (state === 'added') {
        text.dataset.original = text.dataset.original ?? text.textContent ?? '';
        text.textContent = label;
      } else if (!state && text.dataset.original !== undefined) {
        text.textContent = text.dataset.original;
        delete text.dataset.original;
      }
    });
  }

  #onSubmit = () => {
    clearTimeout(this.#addedTimer);
    this.#setCtaState('adding');
  };

  /** @param {Event & {detail?: any, promise?: Promise<unknown>}} event */
  #onCartUpdate = (event) => {
    const source = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    if (source && !this.contains(source)) return;
    const promise = event.promise ?? event.detail?.promise;
    Promise.resolve(promise)
      .then((result) => {
        const failed = result && typeof result === 'object' && 'detail' in result && result.detail?.didError;
        const count = result?.cart?.totalQuantity;
        if (typeof count === 'number') {
          document.querySelectorAll('[data-ag-cart-count]').forEach((el) => { el.textContent = String(count); el.toggleAttribute('data-empty', count === 0); });
        }
        if (failed) return this.#setCtaState('');
        this.#setCtaState('added');
        this.#addedTimer = window.setTimeout(() => this.#setCtaState(''), 1800);
      })
      .catch(() => this.#setCtaState(''));
  };
}

if (!customElements.get('ag-product-component')) {
  customElements.define('ag-product-component', AgProductComponent);
}

/**
 * "Hangs well next to" — loads Shopify's product recommendations for the
 * section through the Section Rendering API and swaps them in.
 */
class AgRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.performed === 'true') return;
    const io = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    io.observe(this);
  }

  async #load() {
    const url = this.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('ag-recommendations');
      if (!next || next.dataset.count === '0') {
        this.hidden = true;
        return;
      }
      this.replaceChildren(...Array.from(next.childNodes));
      this.dataset.performed = 'true';
      this.hidden = false;
    } catch (error) {
      console.warn('[ag-recommendations]', error);
    }
  }
}

if (!customElements.get('ag-recommendations')) {
  customElements.define('ag-recommendations', AgRecommendations);
}

/** Compare cards mirror the selected finish. */
document.addEventListener('ag:variant', (event) => {
  const finish = /** @type {CustomEvent} */ (event).detail?.finish;
  document.querySelectorAll('[data-ag-compare-finish]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    const selected = card.dataset.agCompareFinish === finish;
    card.classList.toggle('is-selected', selected);
    const button = card.querySelector('[data-ag-pick-finish]');
    if (button instanceof HTMLElement) {
      button.textContent = selected ? button.dataset.selectedLabel || '' : button.dataset.label || '';
    }
  });
});
